import validateEmail from "@core/utils/validateEmail";
import {
	GesCloudApi,
	type GesCloudEmailCodeThrottleReason,
	type GesCloudSentEmailCode,
} from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { Button, LoadingButtonTemplate } from "@ui-kit/Button";
import { Input } from "@ui-kit/Input";
import { type FormEvent, useState } from "react";
import { formatCountdown, useCountdown } from "./useCountdown";

type RequestError = "invalidEmail" | "sendFailed" | null;

const getErrorMessage = (error: Exclude<RequestError, null>) =>
	error === "invalidEmail"
		? t("enterprise-cloud-guest.email-code.errors.invalidEmail")
		: t("enterprise-cloud-guest.email-code.errors.sendFailed");

const getThrottleMessage = (reason: GesCloudEmailCodeThrottleReason) =>
	reason === "serviceBusy"
		? t("enterprise-cloud-guest.email-code.serviceBusy")
		: t("enterprise-cloud-guest.email-code.recentlySent");

const getSubmitLabel = (retryAfterSeconds: number, error: RequestError) => {
	if (retryAfterSeconds > 0)
		return t("enterprise-cloud-guest.email-code.retryIn").replace("{time}", formatCountdown(retryAfterSeconds));
	if (error === "sendFailed") return t("enterprise-cloud-guest.email-code.tryAgain");
	return t("enterprise-cloud-guest.email-code.sendCode");
};

interface GesCloudEmailCodeRequestProps {
	gesCloudUrl: string;
	defaultEmail: string;
	onCodeSent: (email: string, sentCode: GesCloudSentEmailCode) => void;
}

export const GesCloudEmailCodeRequest = ({ gesCloudUrl, defaultEmail, onCodeSent }: GesCloudEmailCodeRequestProps) => {
	const [email, setEmail] = useState(defaultEmail);
	const [isSending, setIsSending] = useState(false);
	const [error, setError] = useState<RequestError>(null);
	const [retryAfterSeconds, setRetryAfterSeconds] = useCountdown(0);
	const [throttleReason, setThrottleReason] = useState<GesCloudEmailCodeThrottleReason>("tooFrequent");

	const sendCode = async (event: FormEvent) => {
		event.preventDefault();
		const trimmedEmail = email.trim();
		if (!validateEmail(trimmedEmail)) {
			setError("invalidEmail");
			return;
		}

		setIsSending(true);
		setError(null);
		try {
			const result = await new GesCloudApi(gesCloudUrl).sendEmailCode(trimmedEmail);
			if (result.resultType === "sent") onCodeSent(trimmedEmail, result);
			else if (result.reason === "invalidEmail") setError("invalidEmail");
			else {
				setThrottleReason(result.reason);
				setRetryAfterSeconds(result.retryAfterSeconds);
			}
		} catch {
			setError("sendFailed");
		} finally {
			setIsSending(false);
		}
	};

	const onEmailChange = (value: string) => {
		setEmail(value);
		setError(null);
		setRetryAfterSeconds(0);
	};

	return (
		<form className="flex flex-col gap-3" noValidate onSubmit={sendCode}>
			<div className="flex flex-col gap-1.5">
				<Input
					aria-invalid={error === "invalidEmail"}
					aria-label={t("enterprise-cloud-guest.email-code.emailLabel")}
					autoComplete="email"
					autoFocus
					data-qa="ges-cloud-email-code-email"
					disabled={isSending}
					onChange={(event) => onEmailChange(event.target.value)}
					placeholder={t("enterprise-cloud-guest.email-code.emailPlaceholder")}
					type="email"
					value={email}
				/>
				{error && <p className="text-sm text-status-error">{getErrorMessage(error)}</p>}
			</div>
			{isSending ? (
				<LoadingButtonTemplate className="w-full" text={t("enterprise-cloud-guest.email-code.sending")} />
			) : (
				<Button className="w-full" disabled={retryAfterSeconds > 0} type="submit">
					{getSubmitLabel(retryAfterSeconds, error)}
				</Button>
			)}
			{retryAfterSeconds > 0 && (
				<p className="text-center text-sm text-muted">{getThrottleMessage(throttleReason)}</p>
			)}
		</form>
	);
};
